import { TextFieldModule } from '@angular/cdk/text-field'
import { CommonModule } from '@angular/common'
import { Component, inject } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { AssetsManagerComponent } from '@components/custom-fields'
import { EBvFieldType } from '@modules'
import { AuthorizationService } from '@services'
import { FormatFieldNamePipe } from '@ziptility/pipes'
import { FeatureLinksManagerComponent } from './links-manager/links-manager.component'
import { AssetProfileService } from '@ziptility/feature/asset-profile.service'

@Component({
  selector: 'feature-details',
  template: `
    <div class="py-10 px-12 flex flex-col mb-24">
      @if (vm.form) {
        <form [formGroup]="vm.form">
          @for (control of vm.controls(); track control.id; let i = $index) {
            <div class="mb-2">
              <div class="grid gap-[24px] grid-cols-5">
                <div class="col-span-1">
                  <div class="font-semibold text-sm pt-1">
                    {{ control.label | format_field_name }}
                    @if (control.isRequired) {
                      <div class="required-badge">Required</div>
                    }
                  </div>
                  @if (control.description) {
                    <div class="control-subtitle">
                      {{ control.description }}
                    </div>
                  }
                </div>
                <div class="col-span-4">
                  @switch (control.type) {
                    @case (EBvFieldType.Text) {
                      <input role="input" class="form-input w-full" [formControlName]="control.key" type="text" />
                    }
                    @case (EBvFieldType.Number) {
                      <input role="input" class="form-input w-full" [formControlName]="control.key" type="number" />
                    }
                    @case (EBvFieldType.Textarea) {
                      <textarea
                        class="outline-none form-input w-full"
                        cdkTextareaAutosize
                        [cdkAutosizeMinRows]="3"
                        [cdkAutosizeMaxRows]="6"
                        [formControlName]="control.key"
                      >
                      </textarea>
                    }
                    @case (EBvFieldType.Select) {
                      <select [formControlName]="control.key" class="cursor-pointer w-full form-input outline-none">
                        <option value="">---</option>
                        @for (option of control.selectOptions; track option.id) {
                          <option [ngValue]="option.value">
                            @if (option.imageData) {
                              <img class="feature-symbol" [src]="'data:image/png;base64,' + option.imageData" alt="" />
                            }
                            {{ option.title }}
                          </option>
                        }
                      </select>
                    }
                    @case (EBvFieldType.Date) {
                      <div class="date-picker-container">
                        <input
                          type="text"
                          class="outline-none form-input w-full"
                          [formControlName]="control.key"
                          [matDatepicker]="picker"
                          name="date-picker"
                        />
                        <span class="picker-icon fa fa-calendar" (click)="picker.open()"></span>
                        <mat-datepicker #picker></mat-datepicker>
                      </div>
                    }
                    @case (EBvFieldType.Checkbox) {
                      <input type="checkbox" [formControlName]="control.key" />
                    }
                    @case (EBvFieldType.FeatureLink) {
                      <feature-links-manager
                        [disabled]="control.disabled"
                        [layerPropertyId]="control.layerPropertyId"
                        [targetLayerId]="control.targetLayerId"
                        [featureId]="control.featureId"
                        [allowMultiple]="control.allowMultiple"
                      ></feature-links-manager>
                    }
                    @case (EBvFieldType.Attachment) {
                      <assets-manager
                        [formControlName]="control.key"
                        [allowMultiple]="control.allowMultiple"
                        [disabled]="
                          !this.authorizationService.canManageLayerProperty(control.layerId, control.layerPropertyId)
                        "
                        [deleteHandler]="getDeleteHandler(control)"
                        (onUpload)="vm.onSelectAttachments($event, control)"
                      ></assets-manager>
                    }
                  }
                </div>
              </div>
            </div>
          }
        </form>
      }
    </div>
    @if (vm.hasUnsavedChanges()) {
      <div class="p-5 py-4 absolute bottom-0 right-0 left-0 w-full border-gray-200 bg-white border-t">
        <button
          class="px-4 py-2 rounded w-full font-semibold bg-red-brand text-white"
          [ngClass]="{
            'opacity-50': vm.disabled()
          }"
          (click)="vm.onSave()"
          [disabled]="vm.disabled()"
        >
          {{ vm.feature().id ? 'Save' : 'Create' }}
        </button>
      </div>
    }
  `,
  styleUrls: ['./details.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    TextFieldModule,
    FormatFieldNamePipe,
    AssetsManagerComponent,
    FeatureLinksManagerComponent,
  ],
})
export class FeatureDetailsComponent {
  EBvFieldType = EBvFieldType
  readonly authorizationService = inject(AuthorizationService)
  readonly vm = inject(AssetProfileService)

  getDeleteHandler(control: any) {
    return async (assetId: string) => {
      await this.vm.onDeleteAttachment(assetId, control)
    }
  }
}
